import { isInIframe } from '@shared/utils/isInIframe';
import type { EventDesignFragment } from '@graphql/fragments/eventDesign.graphql.generated';
import type { EventDesignPreferenceRangeFragment } from '@graphql/fragments/eventDesignPreferences.graphql.generated';
import { deriveWebsiteColorsFromEventDesign, parseDesignPreferences } from '@shared/utils/websiteDesign';
import { googleFontFamilies } from '../../common/fonts';
import type { GuestSiteEventDesign, LayoutConfigBrannan, LayoutThemeOverrides, ApplicatorConfigVariantsBrannan } from '../layout.types';
import chroma from 'chroma-js';
import { useStyleApplicator } from '../../common/utils/useStyleApplicator';
import { useBundledApplicatorEnabled } from '@shared/utils/styleApplicator/ThemeStylesProvider';
import { useThemeCanvas } from '@shared/utils/styleApplicator/useThemeCanvas';
import type { ApplicationContext } from '@shared/utils/styleApplicator/styleApplicator.types';
import type { ThemeCanvasResult } from '@shared/utils/styleApplicator/useThemeCanvas';
// Direct source import, not the '@shared/core/website/theming' barrel: the barrel's
// EventDesignThemingProvider reaches layout.constants (via useEventFontsLoader), which
// imports this file — going through index.ts would close that cycle.
import { useEventDesignThemingProvider } from '@shared/core/website/theming/EventDesignThemingContext';
import type { Theme } from '@withjoy/joykit';
import { defaultTheme } from '@withjoy/joykit';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { useCallback, useMemo, useState } from 'react';
import globalWindow from '@shared/core/globals';
import type { Colors } from '@withjoy/joykit/theme';
import { createGlobalStyle } from '@withjoy/joykit/theme';
import memoizeOne from 'memoize-one';
import type { CreateThemeArgs } from '@withjoy/joykit/theme';
import { createTheme } from '@withjoy/joykit/theme';
import { createTextStyleVariantBuilder, pxToRem } from '@shared/joykit/packages/core/common/themes/shared/typography';
import { mergeTypograhyThemeOverrides } from '../../common/theming';
import { useFeatureValue, useIsFeatureEnabled } from '@shared/core/featureFlags';
import { getActiveColor, getHoverColor } from '../layout.utils';

const isIframed = isInIframe();

const BRANNAN_THEME_DEFAULTS: CreateThemeArgs['defaults'] = {
  breakpointWidths: [isIframed ? 375 : 480, 960, 1024, 1440, 1920],
};

export const getBrannanMobileMediaQuery = (theme: Theme) => theme.mediaQueries.between(0, { breakpointAlias: 'sm4' });

const generateThemableColorValues = memoizeOne((color?: string): DeepPartial<Colors> => {
  if (color) {
    const colorHover = getHoverColor(color);
    const colorActive = getActiveColor(color);

    return {
      linkVisited: chroma(color).darken(0.3).hex(),
      linkText: color,
      linkHover: colorHover,
      linkActive: colorActive,

      buttonPrimaryText: '#FFFFFF',
      buttonPrimaryFill: color,
      buttonPrimaryHover: colorHover,
      buttonPrimaryActive: colorActive,

      scrollbarHandle: `rgba(${chroma(color).alpha(0.5).rgba().join(', ')})`,

      buttonOutlinePrimaryText: color,
      buttonOutlinePrimaryTextHover: colorHover,
      buttonOutlinePrimaryTextPress: colorActive,
      buttonOutlinePrimaryFillHover: chroma(color).alpha(0.1).css(),
      buttonOutlinePrimaryFillPress: chroma(color).alpha(0.2).css(),
    };
  }
  return {};
});

export const getBrannanThemeOverrides = (overrides: LayoutThemeOverrides = {}): DeepPartial<Theme> => {
  const {
    eventFontFamily = googleFontFamilies.greatVibes,
    bodyFontFamily = googleFontFamilies.notoSerif,
    hedFontFamily = googleFontFamilies.notoSerif,
    textTransform = 'none',
  } = overrides.typography || {};
  const { primaryFillColor } = overrides.colors || {};

  const themableColors: DeepPartial<Colors> = generateThemableColorValues(primaryFillColor);

  const buildVariantBody = createTextStyleVariantBuilder(bodyFontFamily);
  const buildVariantHed = createTextStyleVariantBuilder(hedFontFamily);

  const typography = mergeTypograhyThemeOverrides({
    variants: {
      body4: buildVariantBody(400, 17, 1.5, 0),
      label2: buildVariantBody(400, 13, 1.4, 0),

      hed2: buildVariantHed(700, 17, 1.36, 0.02),
      hed3: buildVariantHed(500, 20, 1.36, 0),
      hed4: {
        ...buildVariantHed(400, 20, 1.4, 0, { casing: 'uppercase' }),
        letterSpacing: '0.16em',
      },
      // same font-family as aloha (default themeing.ts file)
      hed5: {
        fontSize: pxToRem(24),
        lineHeight: 1.3,
      },
      // same font-family as aloha (default themeing.ts file)
      hed6: {
        fontSize: pxToRem(24),
        lineHeight: 1.3,
        letterSpacing: '-0.01em',
      },

      display1: {
        fontFamily: eventFontFamily,
        lineHeight: 1.23,
        textTransform,
      },
      display2: {
        fontFamily: eventFontFamily,
        textTransform,
      },
      display3: {
        fontFamily: eventFontFamily,
        textTransform,
      },
      display4: {
        fontFamily: eventFontFamily,
        textTransform,
      },
      display5: {
        fontFamily: eventFontFamily,
        textTransform,
      },
      display6: {
        fontFamily: eventFontFamily,
        textTransform,
      },
      display7: {
        fontFamily: eventFontFamily,
        textTransform,
        lineHeight: 1.25,
      },
      display8: {
        fontFamily: eventFontFamily,
        textTransform,
        lineHeight: 1.25,
      },
      display9: {
        fontFamily: eventFontFamily,
        textTransform,
        lineHeight: 1.25,
      },
      display10: {
        fontFamily: eventFontFamily,
        textTransform,
        lineHeight: 1.25,
      },
      display11: {
        fontFamily: eventFontFamily,
        textTransform,
        lineHeight: 1.25,
      },
    },
  });

  return {
    typography,
    colors: {
      ...themableColors,
    },
  };
};

export const brannanConfig: LayoutConfigBrannan = {
  applicatorHtmlProps: {
    layerMiddle: {
      prop: { 'data-layer': 'middle' },
      selector: '[data-layer="middle"]',
    },
    layerBack: {
      prop: { 'data-layer': 'back' },
      selector: '[data-layer="back"]',
    },
    componentTitleBanner: {
      prop: { 'data-component': 'title-banner' },
      selector: '[data-component="title-banner"]',
    },
  },
  theme: createTheme({ defaults: BRANNAN_THEME_DEFAULTS, overrides: getBrannanThemeOverrides() }), // extendTheme(getBrannanThemeOverrides()),
  configureThemeOverrides: eventDesign => {
    const { font } = eventDesign;
    const { accentColor } = deriveWebsiteColorsFromEventDesign(eventDesign, eventDesign.theme);
    const overrides = getBrannanThemeOverrides({
      typography: { eventFontFamily: font.fontFamily, textTransform: font.textTransform },
      colors: { primaryFillColor: accentColor },
    });
    return createTheme({ defaults: BRANNAN_THEME_DEFAULTS, overrides });
  },
  fonts: {
    google: ['greatVibes', 'notoSerif'],
  },
  previewEventHandlers: {
    onTemplateChange: ({ history, eventPageRoutes }) => {
      if (eventPageRoutes.welcome) {
        history.push({ pathname: eventPageRoutes.welcome.path });
      }
      globalWindow.scrollTo?.(0, 0);
    },
    onLayoutChange: ({ history, eventPageRoutes }) => {
      if (eventPageRoutes.welcome) {
        history.push({ pathname: eventPageRoutes.welcome.path });
      }
      globalWindow.scrollTo?.(0, 0);
    },
    onThemeChange: () => {
      globalWindow.scrollTo?.(0, 0);
    },
    onFontChange: () => {
      globalWindow.scrollTo?.(0, 0);
    },
    onGraphicAccentChange: () => {
      const el = globalWindow.document?.getElementById('graphic-accent');
      el?.scrollIntoView();
    },
    onPrimaryFillColorChange: ({ history, eventPageRoutes }) => {
      if (eventPageRoutes.schedule) {
        // Navigate to schedule page and scroll to schedule section
        // If schedule element exists, scroll smoothly to it
        // Otherwise, listen for route change, wait for element to render, then scroll

        const el = globalWindow.document?.getElementById('schedule');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        } else {
          const historySub = history.listen(() => {
            globalWindow.setTimeout(() => {
              const el = globalWindow.document?.getElementById('schedule');
              el?.scrollIntoView();
            }, 500);
            historySub();
          });
          history.push({ pathname: eventPageRoutes.schedule.path });
          return [historySub];
        }
      }
      return [];
    },
  },
};

////////////////////////////////////////////////////////////////

const _isBackgroundTransparencyEnabled = (
  preference?: EventDesignPreferenceRangeFragment,
  websiteLayoutBrannanSettings?: EventDesignFragment['theme']['websiteLayoutBrannanSettings'],
  isCustomArtEnabled?: boolean,
) => {
  const brannanBackgroundTransparency = websiteLayoutBrannanSettings?.brannanBackgroundTransparency;
  // For custom art, enable background transparency irrespective of support for customization
  return brannanBackgroundTransparency?.supportsCustomization || isCustomArtEnabled
    ? preference
      ? preference.rangeValue < 1
      : false
    : brannanBackgroundTransparency
      ? brannanBackgroundTransparency.rangeValue <= 0.7
      : false;
};

export const getBrannanApplicatorConfig = (
  eventDesign: NonNullable<GuestSiteEventDesign>,
  isBypassWebsiteThemeCustomizationEnabled: boolean,
  isMatchDesktopPageColorForMobileTitleBannerEnabled: boolean,
): ApplicatorConfigVariantsBrannan => {
  const { brannanBackgroundTransparency, brannanInnerFrameBorder } = parseDesignPreferences(eventDesign.designPreferences);

  const derivedEventColors = deriveWebsiteColorsFromEventDesign(eventDesign, eventDesign.theme);

  const artBackgroundFontColor = chroma(derivedEventColors.artBackgroundFontColor).rgba();
  const artBackgroundColor = chroma(derivedEventColors.artBackgroundColor).rgba();
  const pageColor = chroma(derivedEventColors.pageColor).rgba();

  const titleBannerBackgroundColor = isMatchDesktopPageColorForMobileTitleBannerEnabled ? pageColor : artBackgroundColor;

  // Note that we check isAlohaGuestPage inside brannan because we use Brannan layout on the guest registry
  // page no matter which template is chosen. See https://withjoy.atlassian.net/browse/COMMERCE-284
  const applicatorConfig: ApplicatorConfigVariantsBrannan = {
    layerBack: {
      customBackgroundColor: {
        isEnabled: true,
        textColor: artBackgroundFontColor,
        backgroundColor: artBackgroundColor,
      },
    },
    layerMiddle: {
      border: {
        isEnabled: brannanInnerFrameBorder?.booleanValue ?? true,
      },
      backgroundTranslucency: {
        isEnabled: _isBackgroundTransparencyEnabled(brannanBackgroundTransparency, eventDesign.theme?.websiteLayoutBrannanSettings, eventDesign.useCustomTheme),
      },
    },
    componentTitleBanner: {
      customBackgroundColor: {
        isEnabled: true,
        // NOTE: `title-banner` component will have its own text color in the future
        textColor: artBackgroundFontColor,
        backgroundColor: titleBannerBackgroundColor,
        bypassThemeCustomization: isBypassWebsiteThemeCustomizationEnabled,
      },
    },
  };

  return applicatorConfig;
};
////////////////////////////////////////////////////////////////

// One component type for the app's lifetime — the CSS arrives as a prop, so editing it
// restyles in place. Building it per render instead would remount the <style> tag each time.
export const CustomCssGlobalStyle = createGlobalStyle<{ $customCss: string }>`
  ${({ $customCss }) => $customCss}
`;

// Read-only design values — never invokes the style applicator, so any number of
// components may call it (Shell and other readers use this).
export const useBrannanDesignValues = (eventDesign: GuestSiteEventDesign) => {
  const [isBypassWebsiteThemeCustomizationEnabled] = useIsFeatureEnabled({ eventFeatureFlagKey: 'bypassWebsiteThemeCustomization' });
  // Flips the mobile title-banner background from `artBackgroundColor` to `pageColor`.
  // Title text still uses `artBackgroundFontColor` — eyeball the mobile title for contrast
  // when enabling per-event, and have the couple re-pick "Color on top of art" if needed.
  const [isMatchDesktopPageColorForMobileTitleBannerEnabled] = useIsFeatureEnabled({ eventFeatureFlagKey: 'matchDesktopPageColorForMobileTitleBanner' });

  const { customCss } = eventDesign || {};
  // useResponsive is SSR-aware (derives default from defaultScreen context).
  // sm4 = 960px — isSmallScreen is true when viewport ≤ 960px.
  const [isSmallScreen] = useResponsive({ values: { _: true, sm4: false } });

  // Get applicator config based on user design preferences
  const applicatorConfig = useMemo(() => {
    return eventDesign ? getBrannanApplicatorConfig(eventDesign, isBypassWebsiteThemeCustomizationEnabled, isMatchDesktopPageColorForMobileTitleBannerEnabled) : undefined;
  }, [eventDesign, isBypassWebsiteThemeCustomizationEnabled, isMatchDesktopPageColorForMobileTitleBannerEnabled]);

  return { isSmallScreen, applicatorConfig, customCss };
};

// The style-applicator WRITER. Exactly one instance may run per page: the applicator's
// "already painted?" check happens only after it downloads the art, so overlapping writers
// on the same [data-layer]/[data-component] elements double-paint the translucent art (or
// wipe it when one instance de-dupes and then cleans up). The page-level component owns
// this call; children read via useBrannanDesignValues instead. PRNT-4856.
export const useBrannanDesign = (_eventHandle: string, eventDesign: GuestSiteEventDesign) => {
  // FeatureFlag => wait for StyleApplicator https://github.com/joylifeinc/joy-web/pull/3214
  const styledApplicatorLoading = useFeatureValue('enableLoadingStyleApplicator');
  // The page's latched decision, not a live flag read: a late resolution would otherwise flip
  // this branch to 'bundled' while the latched provider keeps useThemeCanvas off, leaving the
  // legacy writer with nothing to do and the page with no theme art at all.
  const useBundledApplicator = useBundledApplicatorEnabled();
  const { isSmallScreen, applicatorConfig, customCss } = useBrannanDesignValues(eventDesign);
  const themeId = eventDesign?.theme.themeId;
  // Bundled path: sprites render as React (Shell frames / EventNameNav banner); this fetch is
  // shared with theirs via fetchThemeStyles' cache, so it only supplies the loading signal.
  const backCanvas = useThemeCanvas(useBundledApplicator ? themeId : undefined, BRANNAN_BACK_CONTEXT, applicatorConfig?.layerBack);

  // Incrementing designVersion forces useStyleApplicator to re-run.
  // Used by lazy-loaded header components to re-apply sprites after mount.
  const [designVersion, setDesignVersion] = useState(0);
  const reapplyDesign = useCallback(() => setDesignVersion(v => v + 1), []);

  const { loading } = useStyleApplicator(
    styleApplicator => {
      // Bundled path: Shell's frames and EventNameNav's banner render these sprites as React, so
      // there is nothing for the imperative applicator to gather. Legacy branch below unchanged.
      if (useBundledApplicator) {
        return [];
      }
      if (themeId) {
        const { layerBack, layerMiddle, componentTitleBanner } = brannanConfig.applicatorHtmlProps;

        // On small screens, apply styles to header
        if (isSmallScreen) {
          return [styleApplicator.applyComponentSprites(componentTitleBanner.selector, themeId, 'title-banner', applicatorConfig?.componentTitleBanner)];
        }

        // Apply styles to frames
        return [
          styleApplicator.applyCompositionSprites(layerBack.selector, themeId, 'stacked', 'back', applicatorConfig?.layerBack),
          styleApplicator.applyCompositionSprites(layerMiddle.selector, themeId, 'stacked', 'middle', applicatorConfig?.layerMiddle),
        ];
      }
      return [];
    },
    // designVersion is a trigger dep — not referenced in the callback, but forces a re-run
    // when lazy-loaded components call reapplyDesign() after mounting.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [themeId, isSmallScreen, applicatorConfig, designVersion],
  );

  const effectiveLoading = useBundledApplicator ? backCanvas.loading : loading;
  return { isSmallScreen, applicatorConfig, reapplyDesign, loading: styledApplicatorLoading ? effectiveLoading : false, customCss };
};

export const BRANNAN_BACK_CONTEXT: ApplicationContext = { composition: 'stacked', layer: 'back' };
const TITLE_BANNER_CONTEXT: ApplicationContext = { component: 'title-banner' };

// `@media ` stripped: these feed <source media> and <link media>, which take the bare query.
const asMediaAttribute = (query: string): string => query.replace(/^@media\s+/, '');

/**
 * The viewport ranges Brannan's theme-art canvases are on screen for (bundled path, PRNT-4898):
 * the stacked frames from sm4 (960px) up, the title banner below it — `isSmallScreen` in CSS
 * terms. Passed to ThemeSprites as `media` so a canvas the server rendered from a wrong
 * User-Agent guess resolves to a blank image instead of downloading art hydration then discards.
 * The server's preload links (themeArtPreload.tsx) hardcode the same two strings because they
 * cannot import joykit; LayoutBrannan.theme.spec pins these to those literals.
 */
export const BRANNAN_FRAMES_MEDIA = asMediaAttribute(defaultTheme.mediaQueries.up({ breakpointAlias: 'sm4' }));
export const BRANNAN_TITLE_BANNER_MEDIA = asMediaAttribute(defaultTheme.mediaQueries.between(0, { breakpointAlias: 'sm4' }));

/**
 * Theme art for the small-screen title banner on the bundled path (PRNT-4898). Self-sufficient
 * so the header tree doesn't have to drill design props: reads eventDesign from the theming
 * provider. Inert (empty) with the flag off/unresolved, on large screens, and while loading —
 * the legacy applicator owns the banner node in all those cases.
 */
export const useTitleBannerCanvas = (): ThemeCanvasResult => {
  // Non-strict: the header tree may render without EventDesignThemingProvider (a story, a test,
  // a future admin preview). With no design there is no canvas, so the hook simply goes inert —
  // on both flag arms — instead of taking the header down with a missing-provider throw.
  const themingContext = useEventDesignThemingProvider(false) as ReturnType<typeof useEventDesignThemingProvider> | undefined;
  // The theming provider carries the same eventDesign object the layout hooks receive.
  const design = (themingContext?.eventDesign ?? undefined) as GuestSiteEventDesign;
  const { isSmallScreen, applicatorConfig } = useBrannanDesignValues(design);
  return useThemeCanvas(isSmallScreen ? design?.theme.themeId : undefined, TITLE_BANNER_CONTEXT, applicatorConfig?.componentTitleBanner);
};
